import { Button, Tag, Text, Tooltip } from "@aurem/ui";
import { useI18n } from "providers/I18nProvider";
import { WorkflowConfigFetchStatus } from "types";

const SHORT_SHA_LENGTH = 7;

export const WorkflowConfigFetchTag = ({
  status,
  error,
  commitSha,
  onRetry,
  retrying,
}: {
  status: WorkflowConfigFetchStatus | null | undefined;
  error: string | null | undefined;
  commitSha: string | null | undefined;
  onRetry?: () => void;
  retrying?: boolean;
}) => {
  const { t } = useI18n();

  const labels: Record<WorkflowConfigFetchStatus, string> = {
    Ok: t("apps.admin.internalResources.workflowRun.configFetch.available"),
    NotFound: t(
      "apps.admin.internalResources.workflowRun.configFetch.notFound",
    ),
    ParseFailed: t(
      "apps.admin.internalResources.workflowRun.configFetch.parseFailed",
    ),
    FetchFailed: t(
      "apps.admin.internalResources.workflowRun.configFetch.fetchFailed",
    ),
  };

  const colors: Record<WorkflowConfigFetchStatus, "success" | "danger"> = {
    Ok: "success",
    NotFound: "danger",
    ParseFailed: "danger",
    FetchFailed: "danger",
  };

  const label = status
    ? labels[status]
    : t("apps.admin.internalResources.workflowRun.configFetch.notChecked");
  const color = status ? colors[status] : "neutral";

  const canRetry =
    onRetry !== undefined &&
    Boolean(commitSha) &&
    status !== WorkflowConfigFetchStatus.Ok;

  return (
    <div className="flex items-center gap-2">
      <Tooltip
        content={
          <div className="flex flex-col gap-1 max-w-80">
            {commitSha && (
              <Text size="xs">
                {t(
                  "apps.admin.internalResources.workflowRun.configFetch.commitSha",
                  commitSha.slice(0, SHORT_SHA_LENGTH),
                )}
              </Text>
            )}
            {error && <Text size="xs">{error}</Text>}
          </div>
        }
      >
        <div className="inline-flex cursor-pointer">
          <Tag size="sm" color={color} label={label} />
        </div>
      </Tooltip>
      {canRetry && (
        <Button
          size="xs"
          variant="outlined"
          loading={retrying}
          onClick={onRetry}
        >
          {t("apps.admin.internalResources.workflowRun.configFetch.retry")}
        </Button>
      )}
    </div>
  );
};
